/**
 * `useTranslate` — resolve a shell-copy message key against the active
 * locale.
 *
 * Reads the active language from `useLanguage()` (which itself resolves from
 * `PortsContext`), looks up the matching bundle, and returns a typed lookup
 * function: `(key: MessageKeys) => string`.
 *
 * The return value is a **function**, not a direct string, so that the same
 * hook call can resolve multiple keys in a single component without N hook
 * calls. The function is stable within a given language — it is
 * `useCallback`'d against `active` — so passing `t` down as a prop or
 * including it in an effect's dependency array is safe.
 *
 * **Scope**: shell copy only — nav labels, control labels, demo-transparency
 * labels, placeholder labels (spec §Clarifications, FR-040, per the
 * 2026-04-23 decision-log amendment). Fixture article bodies and Lakikaveri canned responses do NOT
 * round-trip through this hook; they live in their fixture adapters.
 *
 * **Missing key behaviour**: the `MessageKeys` type union makes it a
 * compile-time error to pass an unknown key, so there is no runtime "unknown
 * key" fallback path. If the type guarantee is ever bypassed (via `as
 * MessageKeys` or similar), the bundle lookup returns `undefined` and the
 * function falls back to returning the key itself so the UI surfaces the
 * problem loudly rather than rendering `undefined` into the DOM.
 */

import { useCallback } from 'react';

import { bundles, type MessageKeys } from './messages/index.js';
import { useLanguage } from './useLanguage.js';

export type Translate = (key: MessageKeys) => string;

export function useTranslate(): Translate {
  const { active } = useLanguage();

  // react-compiler-exception: `t` is passed as a prop / effect dependency — identity must be stable per locale
  return useCallback<Translate>(
    (key) => {
      const bundle = bundles[active];
      const value = bundle[key];
      // Runtime defence — see comment above. The `?? key` fallback keeps the
      // UI loud if the type invariant is ever bypassed.
      return value ?? key;
    },
    [active],
  );
}
